import { Component } from 'react';
import { Sparkle } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { Empty, EmptyMedia, EmptyTitle, EmptyDescription, EmptyContent, EmptyHeader } from '../ui/empty';
import { hasRetried, reloadPage } from './_helpers';
import { Error } from '@/app/ui/error';
import { Spinner } from '@/app/ui/spinner';

class CodeSplitErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, countdown: 3 };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Only report once the page has already reloaded and the error persists. A
    // first-time failure is most likely a stale code-split chunk from a new
    // deploy, which the reload below resolves — no need to alert Sentry for that.
    if (hasRetried()) {
      Sentry.captureException(error, { extra: { componentStack: errorInfo?.componentStack } });
    }
  }

  componentDidUpdate() {
    if (this.state.hasError && this.state.countdown > 0) {
      setTimeout(() => {
        this.setState((state) => ({ countdown: state.countdown - 1 }));
      }, 1000);
    } else if (this.state.hasError && this.state.countdown === 0 && !hasRetried()) {
      reloadPage();
    }
  }

  render() {
    if (this.state.hasError) {
      if (hasRetried()) {
        return (
          <div className="flex h-screen w-full items-center justify-center max-w-sm mx-auto">
            <Error message="An error occurred loading the app" />
          </div>
        );
      }
      return (
        <div className="flex h-screen w-full items-center justify-center">
          <div>
            <Empty className="border border-dashed">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Sparkle />
                </EmptyMedia>
                <EmptyTitle>New App Version Available</EmptyTitle>
                <EmptyDescription>A fresh update is ready with improvements and new features.</EmptyDescription>
              </EmptyHeader>
              <EmptyContent>
                <div className="flex gap-2 items-center justify-center">
                  <Spinner />
                  <p>Reloading...</p>
                </div>
              </EmptyContent>
            </Empty>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default CodeSplitErrorBoundary;
